import { useNavigation } from "react-router";
import { useEffect, useState } from "react";

/**
 * TopProgressBar — GPU-composited loading indicator
 *
 * BEFORE: transition-[width] → causes layout recalc every frame (width is not compositable)
 * AFTER:  transform: scaleX() with transform-origin: left → pure GPU composite, zero layout thrash
 *
 * Why scaleX works here:
 *  - The bar div is always 100% wide (no layout change)
 *  - scaleX(0→1) scales it from left, simulating a fill
 *  - Browser compositor handles this without touching the layout tree
 */
export function TopProgressBar() {
    const navigation = useNavigation();
    const [progress, setProgress] = useState(0);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout>;
        let interval: ReturnType<typeof setInterval>;

        if (navigation.state === "loading" || navigation.state === "submitting") {
            timer = setTimeout(() => {
                setVisible(true);
                setProgress(0.2);

                interval = setInterval(() => {
                    setProgress((prev) => {
                        const increment = (1 - prev) * 0.1;
                        return Math.min(prev + increment, 0.9);
                    });
                }, 300);
            }, 50);
        } else if (navigation.state === "idle") {
            setProgress(1);
            timer = setTimeout(() => {
                setVisible(false);
                setTimeout(() => setProgress(0), 300);
            }, 300);
        }

        return () => {
            clearTimeout(timer);
            clearInterval(interval);
        };
    }, [navigation.state]);

    return (
        <div
            className="fixed top-0 left-0 right-0 z-[100] h-1 bg-transparent pointer-events-none overflow-hidden"
            aria-hidden="true"
        >
            <div
                className="h-full w-full bg-brand-500 shadow-[0_0_10px_rgba(59,130,246,0.7)] origin-left"
                style={{
                    // GPU composite path — scaleX never touches the layout tree
                    transform: `scaleX(${progress})`,
                    transformOrigin: "left center",
                    transition: visible
                        ? "transform 300ms ease-out, opacity 0ms"
                        : "transform 300ms ease-out, opacity 300ms ease-in",
                    opacity: visible ? 1 : 0,
                }}
            />
        </div>
    );
}
